"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const projects = [{
  number: "01",
  name: "Ethereal Bridal",
  location: "SOFT RADIANCE, CALIFORNIA",
  year: "2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/bridal_glow_portrait.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/bridal_glow_portrait.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/bridal_glow_portrait.png-wvc-sizes",
  alt: "Ethereal Bridal — soft focus makeup with dewy skin and rose-gold highlights",
  aspect: "aspect-[4/5]",
  layout: "left"
}, {
  number: "02",
  name: "Editorial Edge",
  location: "HIGH FASHION, NEW YORK",
  year: "2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/editorial_sharp_contour.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/editorial_sharp_contour.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/editorial_sharp_contour.png-wvc-sizes",
  alt: "Editorial Edge — high-fashion makeup with sharp contouring and bold textures",
  aspect: "aspect-[4/5]",
  layout: "right-offset"
}, {
  number: "03",
  name: "Transformation",
  location: "BEFORE & AFTER, STUDIO",
  year: "2023",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/skin_transformation_glow.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/skin_transformation_glow.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/skin_transformation_glow.png-wvc-sizes",
  alt: "Transformation — before and after makeup application showcasing skin texture",
  aspect: "aspect-[16/9]",
  layout: "full"
}, {
  number: "04",
  name: "Modern Classic",
  location: "SPECIAL EVENT, LOS ANGELES",
  year: "2023",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/modern_classic_styling.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/modern_classic_styling.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/modern_classic_styling.png-wvc-sizes",
  alt: "Modern Classic — elegant event styling with natural textiles and soft glow",
  aspect: "aspect-[4/5]",
  layout: "center-right"
}];
function ProjectCard({
  project,
  index
}: {
  project: (typeof projects)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -80px 0px"
  });
  return <div ref={ref} data-index={index} className="group relative">
      <div className={`relative overflow-hidden ${project.aspect} bg-muted cursor-pointer`}>
        <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/0 group-hover:bg-background/10" />
      </div>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div className="flex items-start gap-6">
          <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground pt-1">
            {project.number}
          </span>
          <div>
            <h3 className="font-serif italic text-lg text-foreground">
              {project.name}
            </h3>
            <p className="mt-1 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
              {project.location}
            </p>
          </div>
        </div>
        <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground pt-1">
          {project.year}
        </span>
      </div>
    </div>;
}
export default function EditorialEdge() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4952" id="editorial-edge" className="dark bg-background text-foreground py-32 md:py-48 lg:py-[200px] px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8">
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground mb-8">PORTFOLIO</p>
            <h2 className="font-serif font-default text-5xl md:text-6xl lg:text-[80px] leading-[1.05] tracking-tight text-foreground">
              Artistry defined
              <br />
              by radiance.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pt-4">
            <div className="max-w-[280px]">
              <p className="text-base leading-relaxed text-muted-foreground font-default">
                A curated collection of our signature looks — ethereal bridal
                glows and sharp editorial precision. Each transformation is a
                study in light, crafted with organic textures and shaped by
                the unique beauty of every face.
              </p>
              <Link to="/PortfolioPage" className="group inline-flex items-center gap-2 mt-8 font-mono text-[11px] tracking-[0.2em] uppercase text-foreground">
                <span className="relative">
                  View all 24 looks
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-left scale-x-100 group-hover:scale-x-0" />
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-right scale-x-0 group-hover:scale-x-100" />
                </span>
                <ArrowRight className="size-3.5" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-24 md:mt-32 lg:mt-[120px] space-y-24 md:space-y-32">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-7">
              <ProjectCard project={projects[0]} index={0} />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:-mt-12">
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 lg:mt-20">
              <ProjectCard project={projects[1]} index={1} />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:pt-16">
            <div className="col-span-12">
              <ProjectCard project={projects[2]} index={2} />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-6 lg:col-start-5">
              <ProjectCard project={projects[3]} index={3} />
            </div>
          </div>
        </div>

        <div className="mt-32 md:mt-40">
          <Separator className="bg-border" />
          <div className="flex justify-center pt-12">
            <Button asChild variant="link" className="group font-mono text-[11px] tracking-[0.25em] uppercase text-foreground hover:no-underline px-0">
              <Link to="/PortfolioPage" className="inline-flex items-center gap-3">
                <span className="relative">
                  Explore the full gallery
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-left scale-x-0 group-hover:scale-x-100" />
                </span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}